import type { FC } from "react";
import type { MatchEventsComponent } from "@motain/xpa-proto-files-web/lib/types/match_events";
import React, { useRef, useState } from "react";
import classNames from "classnames";

import { TeamSide } from "@motain/xpa-proto-files-web/lib/types/team_side";
import { MatchEventCard } from "./match-event-card/MatchEventCard";
import { normalizeMatchEvents } from "./helpers/normalizeMatchEvents";
import { MatchEventsTimeline } from "./match-events-timeline/MatchEventsTimeline";
import styles from "./MatchEvents.module.scss";
import { Button } from "@/components/button/Button";
import { VideoTeaser } from "@/components/video-teaser/VideoTeaser";

const COLLAPSED_MAX_EVENTS_COUNT = 5;

const MatchEvents: FC<MatchEventsComponent> = (matchEventsComponent) => {
  const normalizedMatchEvents = normalizeMatchEvents(
    matchEventsComponent.events,
  );

  const [isExpanded, setIsExpanded] = useState(false);

  const eventsListRef = useRef<HTMLUListElement | null>(null);

  const toggleList = () => {
    setIsExpanded(!isExpanded);
    if (isExpanded) {
      eventsListRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const getButtonText = () => {
    return isExpanded
      ? matchEventsComponent.toggleButton?.collapseName
      : matchEventsComponent.toggleButton?.expandName;
  };

  const renderMatchList = () => {
    const { matchEventsItem, matchEventsItemHome, matchEventsItemAway } =
      styles;

    return normalizedMatchEvents.map((evt, i) => {
      if (!isExpanded && i > COLLAPSED_MAX_EVENTS_COUNT - 1) {
        return;
      }
      return (
        <li
          key={evt.uiKey}
          className={classNames(
            matchEventsItem,
            evt.side === TeamSide.TEAM_SIDE_HOME
              ? matchEventsItemHome
              : matchEventsItemAway,
          )}
        >
          <MatchEventsTimeline timeline={evt.timeline} />
          <div className={classNames(styles.matchEventsItemBox)}>
            <MatchEventsTimeline timeline={evt.timeline} inner />
            <MatchEventCard matchEvent={evt} />
            <div className={styles.matchEventsItemLiveClipTeaser}>
              {evt.videoLink && <VideoTeaser {...evt.videoLink} />}
            </div>
          </div>
        </li>
      );
    });
  };

  return (
    <div className={styles.matchEvents}>
      <ul className={styles.matchEventsList} ref={eventsListRef}>
        {renderMatchList()}
      </ul>
      {normalizedMatchEvents.length > COLLAPSED_MAX_EVENTS_COUNT && (
        <Button
          className={classNames(
            styles.matchEventsToggleButton,
            styles.matchEventsToggleButtonContent,
          )}
          type="button"
          variant="outline"
          onClick={toggleList}
        >
          {getButtonText()}
        </Button>
      )}
    </div>
  );
};

export { MatchEvents };
